"use client";
import { useEffect, useState, type ReactNode } from "react";
import { Band, StatStrip } from "@/app/dashboard/_sections/Band";
import { Tag } from "@/components/ui/Tag";
import type { Timeframe } from "@/lib/api/types";
import { dashboardContent, fmt } from "@/lib/content";
import { fmtPhaseRange, fmtSpan } from "@/lib/dashboard/format";
import type { DashboardStat } from "@/lib/dashboard/stats";
import { segmentFlex, timelineColumns, timelineStatus, timelineView } from "@/lib/dashboard/timeline";
import { useCountdown } from "@/lib/hooks/useCountdown";
import { cn } from "@/lib/utils";

// The bar moves by a pixel every few hours of a week-long window; the countdown has its
// own one-second clock.
const TICK_MS = 30_000;

/**
 * The dashboard's heading, with the contour rings of the research band on the home page
 * but in the page's own theme, light or dark. It answers the questions someone opens the page with, in the
 * order they ask them - which competition, what phase, how long left - and carries the
 * picker, because switching competitions changes everything below it, not one card.
 *
 * The countdown is the page's one hero figure. It uses tabular digits although it is a
 * large standalone number: it changes every second, and proportional digits would make
 * the whole line twitch as a 1 turns into a 0.
 *
 * The first paint uses `renderedAt` from the server, so the bar is identical on both
 * sides of hydration and only start moving once the browser
 * takes over.
 */
export function DashboardBand({
  name,
  mode,
  timeframe,
  renderedAt,
  stats,
  picker,
}: {
  name: string | undefined;
  mode: "current" | "closing" | "archive" | null;
  timeframe: Timeframe | undefined;
  renderedAt: number;
  stats: DashboardStat[];
  picker?: ReactNode;
}) {
  const t = dashboardContent;
  const [now, setNow] = useState(renderedAt);

  useEffect(() => {
    const tick = () => setNow(Date.now());
    tick();
    const id = setInterval(tick, TICK_MS);
    return () => clearInterval(id);
  }, []);

  const view = timeframe ? timelineView(timeframe, now) : null;
  const countdown = useCountdown(view?.countdown?.at);
  const archived = mode === "archive";
  const status = mode ? timelineStatus(mode, view?.phase ?? null) : null;
  const badge =
    status === null
      ? null
      : status.key === "archive" || status.key === "closing"
        ? t.timeline.modes[status.key]
        : (t.timeline.phases[status.key] ?? "");
  const phaseName = view?.countdown?.phase === "upload" ? t.timeline.upload : t.timeline.evaluation;
  const columns = view ? timelineColumns(view) : [];

  return (
    <Band>
      <p className="eyebrow text-fg-muted">{t.band.eyebrow}</p>

      <div className="mt-6 flex flex-wrap items-end justify-between gap-x-8 gap-y-5">
        <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-3">
          <h1 className="display text-[clamp(30px,4.2vw,46px)] leading-[1.08]">{name ?? t.timeline.empty}</h1>
          {badge && (
            <Tag tone={status?.live ? "accent" : "muted"} dot={status?.live}>
              {badge}
            </Tag>
          )}
        </div>
        {picker && <div className="w-full sm:w-auto sm:min-w-64">{picker}</div>}
      </div>

      {!archived && view && (
        <div className="mt-10 grid gap-6">
          <div>
            <p className="text-[14px] text-fg-muted">
              {view.countdown
                ? fmt(view.countdown.kind === "ends" ? t.timeline.endsIn : t.timeline.startsIn, { phase: phaseName })
                : t.timeline.ended}
            </p>
            {view.countdown && (
              <p className="mt-1 min-h-[1.1em] font-medium tabular-nums text-[clamp(40px,6.4vw,64px)] leading-none tracking-[-0.035em]">
                {countdown ?? " "}
              </p>
            )}
          </div>

          <div className="grid max-w-[720px] gap-3">
            {/* Segments are scaled to real duration and the gap is only drawn when the
                schedule actually has one, so the bar reads as a calendar, not a diagram. */}
            <div className="flex items-center">
              <Bar fill={view.upload.fill} weight={view.weights.upload} />
              {view.gap && <Bar fill={view.gap.fill} weight={view.weights.gap} idle />}
              <Bar fill={view.evaluation.fill} weight={view.weights.evaluation} />
            </div>
            {/* One label per segment, exactly as wide as it: the gap's sits under the gap. */}
            <div className="flex items-start">
              {columns.map((c) => (
                <div
                  key={c.key}
                  className={cn(
                    "relative mx-[1px] min-w-[3px]",
                    c.align === "end" && "text-right",
                    // A short gap is narrower than its own label, which then straddles the
                    // gap's centre; on a phone there is no room beside it at all.
                    c.align === "center" && "hidden sm:block",
                  )}
                  style={{ flex: c.flex }}
                >
                  <div
                    className={cn(
                      "grid gap-1 whitespace-nowrap",
                      c.align === "center" && "absolute left-1/2 -translate-x-1/2 text-center",
                    )}
                  >
                    <span className={cn("eyebrow text-[10px]", c.state === "running" ? "text-accent" : "text-fg-muted")}>
                      {c.key === "upload" ? t.timeline.upload : c.key === "gap" ? t.timeline.gap : t.timeline.evaluation}
                    </span>
                    {c.key === "gap" ? (
                      <span className="text-[12.5px] tabular-nums text-fg-2">
                        {fmtSpan(Date.parse(c.end) - Date.parse(c.start))}
                      </span>
                    ) : (
                      fmtPhaseRange(c.start, c.end).map((line) => (
                        <span key={line} className="text-[12.5px] leading-tight tabular-nums text-fg-2">
                          {line}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <StatStrip items={stats} className="mt-12" />
    </Band>
  );
}

/**
 * One segment of the clock. `weight` is its share of the whole window; a real but tiny
 * phase still gets a sliver rather than collapsing to nothing. An idle segment carries no
 * progress - nothing is happening in it, so nothing fills.
 */
function Bar({ fill, weight, idle = false }: { fill: number; weight: number; idle?: boolean }) {
  return (
    <span
      className={cn(
        "mx-[1px] h-1 min-w-[3px] overflow-hidden rounded-full",
        idle ? "bg-line-soft ring-1 ring-line ring-inset" : "bg-line-strong",
      )}
      style={{ flex: segmentFlex(weight) }}
    >
      {!idle && (
        <span
          className="block h-full rounded-full bg-accent transition-[width] duration-500 ease-soft"
          style={{ width: `${Math.round(fill * 100)}%` }}
        />
      )}
    </span>
  );
}
